import { useState } from 'react'
import { DiscountPicker } from './DiscountPicker'
import { TenderPicker } from './TenderPicker'
import { BahsisPanel } from './BahsisPanel'
import { applyDiscount, formatTRY } from '../../utils/money'
import type { PaymentMethod } from '../../types'

interface Props {
  totalKurus: number
  /** Rates offered here, in percent. Configured per yurt. */
  discounts: number[]
  onConfirm: (
    method: PaymentMethod,
    extras: { note?: string; discountPercent?: number } & BahsisExtras,
    /** Üstü Kalsın'da verilmiyor: kasanın kendi kuralı karar veriyor. */
    print: boolean | undefined,
  ) => void
  onCancel: () => void
  /** Present only for an owner; see DiscountPicker. */
  onSaveRates?: (next: number[]) => Promise<void>
  /** Bahşiş modülü açıksa adı ("Bahşiş"); indirimden sonra Üstü Kalsın için. */
  bahsisLabel?: string
}

/** Üstü kalan para ve nasıl verildiği; ikisi birlikte gelir. */
export interface BahsisExtras {
  bahsisKurus?: number
  bahsisPaymentMethod?: 'cash' | 'card'
}

/**
 * İndirimli satış: önce oran, sonra, Öde & Kapat gibi, nakit mi kart mı.
 *
 * Eskiden oran seçilince satış 'student' ("İndirimli") yöntemiyle kapanıyor,
 * paranın nakit mi kart mı geldiği hiç sorulmuyordu; kasa gün sonunda
 * tutmuyordu (kullanıcı, 2 Eki 2026). İndirim yöntemden bağımsız
 * (completeSale discountPercent'i her yöntemde uyguluyor), raporda da
 * İndirimli kartında ayrıca görünüyor.
 */
export function OtherPaymentSheet({ totalKurus, discounts, onConfirm, onCancel, onSaveRates, bahsisLabel }: Props) {
  return (
    <div className="dlg" role="dialog" aria-modal="true">
      <DiscountTender
        totalKurus={totalKurus}
        discounts={discounts}
        onCancel={onCancel}
        onSaveRates={onSaveRates}
        bahsisLabel={bahsisLabel}
        onConfirm={(method, percent, print, bahsis) =>
          onConfirm(method, { discountPercent: percent, ...bahsis }, print)
        }
      />
    </div>
  )
}

/** Oran → ödeme yöntemi. Kasada ve parçalı ödemede aynı adım. */
export function DiscountTender({
  totalKurus,
  discounts,
  onConfirm,
  onCancel,
  onSaveRates,
  bahsisLabel,
}: {
  totalKurus: number
  discounts: number[]
  onConfirm: (method: PaymentMethod, percent: number, print: boolean | undefined, bahsis?: BahsisExtras) => void
  onCancel: () => void
  onSaveRates?: (next: number[]) => Promise<void>
  bahsisLabel?: string
}) {
  const [picked, setPicked] = useState<{ percent: number; print: boolean } | null>(null)
  /**
   * Üstü Kalsın (kullanıcı, 20 Eki 2026): indirim küsuratlı bir tutar
   * çıkarıyor (214,40), müşteri düz 250 verip "üstü kalsın" diyor. Oran
   * onaylanırken seçiliyor; seçilen oran burada.
   */
  const [tipPercent, setTipPercent] = useState<number | null>(null)

  if (tipPercent !== null && bahsisLabel) {
    const { chargedKurus } = applyDiscount(totalKurus, tipPercent)
    return (
      <div className="dlg__card dconf">
        <div className="dconf__head">
          <h2>İndirim %{tipPercent}</h2>
          <span className="dconf__was">{formatTRY(totalKurus)}</span>
        </div>
        <BahsisPanel
          totalKurus={chargedKurus}
          label={bahsisLabel}
          // Vazgeç oran seçimine döndürüyor, satıştan çıkarmıyor.
          onCancel={() => setTipPercent(null)}
          onConfirm={(bahsisKurus, method) =>
            onConfirm(method, tipPercent, undefined, { bahsisKurus, bahsisPaymentMethod: method })
          }
        />
      </div>
    )
  }

  if (!picked) {
    return (
      <DiscountPicker
        totalKurus={totalKurus}
        discounts={discounts}
        onCancel={onCancel}
        onSaveRates={onSaveRates}
        onConfirm={(percent, print) => setPicked({ percent, print })}
        onTip={bahsisLabel ? setTipPercent : undefined}
      />
    )
  }

  const { chargedKurus } = applyDiscount(totalKurus, picked.percent)
  return (
    <div className="dlg__card dconf">
      <div className="dconf__head">
        <h2>İndirim %{picked.percent}</h2>
        <span className="dconf__was">{formatTRY(totalKurus)}</span>
      </div>
      <TenderPicker
        totalKurus={chargedKurus}
        print={picked.print}
        // Vazgeç orana geri döndürüyor, satıştan çıkarmıyor.
        onCancel={() => setPicked(null)}
        onPick={(method, print) => onConfirm(method, picked.percent, print)}
      />
    </div>
  )
}
